import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import {
  Button,
  Input,
  Select,
  Modal,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import {
  useMovements,
  useCreateMovement,
  useWarehouses,
  type MovementTypeInput,
} from './useInventory';
import { useAuth } from '../../shared/auth/AuthContext';
import { useProductLookup } from '../../shared/lookups/useParties';

type MovementFilter = 'all' | MovementTypeInput;

/* Os valores sao os de `StockMovementType`. O registro gravado usa a forma
   resolvida (TRANSFER_IN/OUT, ADJUSTMENT_IN/OUT); a criacao usa a simples. */
const TYPE_LABELS: Record<string, string> = {
  RECEIVE: 'Entrada',
  ISSUE: 'Saída',
  TRANSFER: 'Transferência',
  TRANSFER_IN: 'Transf. entrada',
  TRANSFER_OUT: 'Transf. saída',
  ADJUSTMENT: 'Ajuste',
  ADJUSTMENT_IN: 'Ajuste (+)',
  ADJUSTMENT_OUT: 'Ajuste (−)',
  COUNT: 'Inventário',
};

/* Pill de movimento como na tela de referencia: icone direcional +
   container de cor por papel. O sentido do movimento tem de ser legivel
   antes de ler o texto: numa lista de kardex e a primeira informacao que
   o operador procura. */
const PILL_BASE =
  'inline-flex items-center gap-1 px-2 py-0.5 rounded-pill font-label-sm text-label-sm';
const MINUS = '\u2212';

const TYPE_PILL: Record<string, { cls: string; icon: string }> = {
  RECEIVE: { cls: 'bg-primary-container text-on-primary-container', icon: 'arrow_downward' },
  TRANSFER_IN: { cls: 'bg-primary-container text-on-primary-container', icon: 'arrow_downward' },
  ADJUSTMENT_IN: { cls: 'bg-surface-variant text-on-surface-variant', icon: 'sync_alt' },
  ISSUE: { cls: 'bg-error-container text-on-error-container', icon: 'arrow_upward' },
  TRANSFER_OUT: { cls: 'bg-error-container text-on-error-container', icon: 'arrow_upward' },
  ADJUSTMENT_OUT: { cls: 'bg-surface-variant text-on-surface-variant', icon: 'sync_alt' },
  ADJUSTMENT: { cls: 'bg-surface-variant text-on-surface-variant', icon: 'sync_alt' },
  COUNT: { cls: 'bg-surface-variant text-on-surface-variant', icon: 'fact_check' },
  /* `warning-container` nao existe no tailwind.config e o prototipo quase
     nao usa tom de aviso (1 ocorrencia em 128 telas). Transferencia usa
     `secondary-container`, que e o container de apoio da referencia
     (132 ocorrencias). */
  transfer: { cls: 'bg-secondary-container text-on-secondary-container', icon: 'swap_horiz' },
};

function MovementBadge({ type }: { type: string }): JSX.Element {
  const pill = TYPE_PILL[type] ?? TYPE_PILL['ADJUSTMENT']!;
  return (
    <span
      className={PILL_BASE + ' ' + pill.cls}
    >
      <span className="material-symbols-outlined text-[14px]">{pill.icon}</span>
      {TYPE_LABELS[type] ?? type}
    </span>
  );
}

/* Sinal e cor da quantidade vem do tipo: o campo `quantity` e sempre
   positivo no backend. */
const OUTBOUND = new Set(['ISSUE', 'TRANSFER_OUT', 'ADJUSTMENT_OUT']);
const INBOUND = new Set(['RECEIVE', 'TRANSFER_IN', 'ADJUSTMENT_IN']);

function signedQuantity(type: string, quantity: number): { text: string; cls: string } {
  if (OUTBOUND.has(type)) return { text: MINUS + quantity, cls: 'text-error' };
  if (INBOUND.has(type)) return { text: '+' + quantity, cls: 'text-primary' };
  return { text: String(quantity), cls: 'text-on-surface-variant' };
}

export function MovementsPage(): JSX.Element {
  const { addToast } = useToast();
  const [page, setPage] = useState(0);
  const [filterType, setFilterType] = useState<MovementFilter>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [formData, setFormData] = useState({
    type: 'RECEIVE' as MovementTypeInput,
    productId: '',
    productLabel: '',
    quantity: '',
    warehouseId: '',
    notes: '',
  });

  const { tenant } = useAuth();
  const companyId = tenant?.id ?? '';
  const warehousesQ = useWarehouses();
  const productsQ = useProductLookup(productSearch);
  const allProductsQ = useQuery({
    queryKey: ['products', 'movement-names'],
    queryFn: async () => apiClient.get<{ id: string; name: string }[]>('/products'),
  });

  const limit = 20;

  const { data, isLoading, error } = useMovements({ page, limit });
  const createMovement = useCreateMovement();

  const movements = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const createData = {
    data: movements,
    total,
    page,
    limit,
  } as const;

  /* O filtro compara a familia do movimento: quem procura "Transferência"
     quer as duas pontas (TRANSFER_IN e TRANSFER_OUT). */
  const inFamily = (type: string, family: MovementTypeInput): boolean =>
    type === family || type.startsWith(family + '_');

  const filteredMovements = filterType === 'all'
    ? createData.data
    : createData.data.filter((m) => inFamily(m.type, filterType));

  /* Nome de produto e de armazem: a resposta traz so os ids. Mesmo padrao do
     StockPage — sem isto as duas colunas ficavam em branco. */
  const warehouseName = (id: string): string =>
    (warehousesQ.data ?? []).find((w) => w.id === id)?.name ?? id;

  /* Produto: a busca do formulario so cobre o que foi digitado, entao a lista
     resolve o nome com uma consulta propria (mesmo padrao do StockPage). */
  const productName = (id: string): string =>
    (allProductsQ.data ?? []).find((p) => p.id === id)?.name ?? id;

  const handleCreate = async () => {
    if (!formData.productId.trim() || !formData.warehouseId.trim() || !formData.quantity.trim()) {
      addToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }

    const quantity = Number(formData.quantity);
    if (Number.isNaN(quantity) || quantity <= 0) {
      addToast('Quantidade inválida', 'error');
      return;
    }

    try {
      await createMovement.mutateAsync({
        companyId,
        warehouseId: formData.warehouseId,
        productId: formData.productId,
        type: formData.type,
        quantity,
        ...(formData.notes ? { notes: formData.notes } : {}),
      });
      addToast('Movimentação registrada com sucesso', 'success');
      setModalOpen(false);
      setProductSearch('');
      setFormData({ type: 'RECEIVE', productId: '', productLabel: '', quantity: '', warehouseId: '', notes: '' });
    } catch {
      addToast('Erro ao registrar movimentação', 'error');
    }
  };

  return (
    <div className="mov-page flex flex-col gap-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Movimentações</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Movimentações de estoque e kardex por produto.</p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Button onClick={() => setModalOpen(true)}>+ Nova Movimentação</Button>
        </div>
      </div>

      {/* Card unico com a faixa do prototipo: titulo a esquerda e o
          controle de filtro dentro da propria faixa. A tela de referencia
          nao usa card separado para o filtro. */}
      <div className="bg-surface-container-lowest rounded-xl border border-outline-variant shadow-sm overflow-hidden flex flex-col">
        <div className="p-md border-b border-outline-variant bg-surface-container-low flex flex-wrap justify-between items-center gap-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
            Histórico de Movimentações (Kardex)
          </h3>
          <div className="flex items-center gap-sm">
            <label
              htmlFor="mov-type"
              className="font-label-md text-label-md text-on-surface-variant"
            >
              Tipo
            </label>
            <select
              id="mov-type"
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value as MovementFilter);
                setPage(0);
              }}
              className="bg-surface border border-outline-variant font-body-sm text-body-sm text-on-surface rounded py-1.5 px-sm focus:ring-1 focus:ring-primary focus:border-primary cursor-pointer"
            >
              <option value="all">Todos</option>
              <option value="in">Entrada</option>
              <option value="out">Saída</option>
              <option value="adjust">Ajuste</option>
              <option value="transfer">Transferência</option>
            </select>
          </div>
        </div>
        <div className="p-md">
          {/* ── Loading ── */}
          {isLoading && (
            <LoadingSkeleton count={6} height="48px" />
          )}

          {/* ── Error ── */}
          {error && (
            <div className="state-error" role="alert">
              <p>Erro ao carregar movimentações</p>
              <p className="state-detail">{(error as Error).message}</p>
            </div>
          )}

          {/* ── Empty ── */}
          {!isLoading && !error && filteredMovements.length === 0 && (
            <div className="state-empty">
              <p>Nenhuma movimentação encontrada</p>
              <p className="state-detail">
                {filterType !== 'all'
                  ? 'Nenhuma movimentação do tipo selecionado.'
                  : 'Registre a primeira movimentação para começar.'}
              </p>
            </div>
          )}

          {/* ── Data ── */}
          {!isLoading && !error && filteredMovements.length > 0 && (
            <>
              <div className="overflow-x-auto -mx-md">
                <table className="w-full text-left border-collapse min-w-[800px]">
                  <thead>
                    <tr className="bg-surface-container-low text-on-surface-variant font-label-md text-label-md uppercase tracking-wider">
                      <th className="pl-md p-sm border-b border-outline-variant font-medium sticky top-0 bg-surface-container-low">Data/Hora</th>
                      <th className="p-sm border-b border-outline-variant font-medium sticky top-0 bg-surface-container-low">Tipo</th>
                      <th className="p-sm border-b border-outline-variant font-medium sticky top-0 bg-surface-container-low">Produto</th>
                      <th className="p-sm border-b border-outline-variant font-medium sticky top-0 bg-surface-container-low">Armazém</th>
                      <th className="p-sm border-b border-outline-variant font-medium sticky top-0 bg-surface-container-low">Origem</th>
                      <th className="pr-md text-right p-sm border-b border-outline-variant font-medium sticky top-0 bg-surface-container-low">Qtd.</th>
                    </tr>
                  </thead>
                  <tbody className="font-body-md text-body-md text-on-surface divide-y divide-outline-variant">
                    {filteredMovements.map((m) => {
                      const qty = signedQuantity(m.type, m.quantity);
                      return (
                        <tr key={m.id} className="hover:bg-surface-container transition-colors">
                          <td className="p-sm pl-md whitespace-nowrap">
                            {new Date(m.createdAt).toLocaleString('pt-BR', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </td>
                          <td className="p-sm"><MovementBadge type={m.type} /></td>
                          <td className="p-sm">{productName(m.productId)}</td>
                          <td className="p-sm">{warehouseName(m.warehouseId)}</td>
                          <td className="p-sm">
                            <span className="font-label-md text-label-md">{m.reference}</span>
                            {m.notes && (
                              <span className="block font-body-sm text-body-sm text-on-surface-variant">
                                {m.notes}
                              </span>
                            )}
                          </td>
                          <td
                            className={'p-sm pr-md text-right font-label-md tabular-nums font-medium ' + qty.cls}
                          >
                            {qty.text}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Pagination ── */}
              {totalPages > 1 && (
                <div className="mov-pagination">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={page <= 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Anterior
                  </Button>
                  <span className="mov-page-info">
                    Página {page + 1} de {totalPages} ({total} registros)
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Create Movement Modal ── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Nova Movimentação"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={createMovement.isPending}>
              Registrar
            </Button>
          </>
        }
      >
        <div className="mov-form">
          <Select
            label="Tipo"
            value={formData.type}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, type: e.target.value as MovementTypeInput }))
            }
          >
            <option value="RECEIVE">Entrada</option>
            <option value="ISSUE">Saída</option>
            <option value="ADJUSTMENT">Ajuste</option>
            <option value="TRANSFER">Transferência</option>
            <option value="COUNT">Inventário</option>
          </Select>
          {formData.productId ? (
            <div className="mov-picked">
              <span>{formData.productLabel}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFormData((prev) => ({ ...prev, productId: '', productLabel: '' }))}
              >
                Trocar
              </Button>
            </div>
          ) : (
            <div>
              <Input
                label="Produto"
                placeholder="Buscar por nome ou SKU (mín. 2 letras)"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
              {(productsQ.data?.length ?? 0) > 0 && (
                <ul className="mov-lookup">
                  {productsQ.data?.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, productId: p.id, productLabel: p.name }));
                          setProductSearch('');
                        }}
                      >
                        <span>{p.name}</span>
                        <span className="mov-mono">{p.sku}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <Input
            label="Quantidade"
            type="number"
            placeholder="Ex: 10"
            value={formData.quantity}
            onChange={(e) => setFormData((prev) => ({ ...prev, quantity: e.target.value }))}
          />
          {/* Armazem e obrigatorio no contrato — o movimento acontece em um
              deposito. Antes o campo dizia "opcional" e o backend recusava. */}
          <Select
            label="Armazém"
            value={formData.warehouseId}
            onChange={(e) => setFormData((prev) => ({ ...prev, warehouseId: e.target.value }))}
          >
            <option value="">Selecione o armazém</option>
            {(warehousesQ.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
          <Input
            label="Observação (opcional)"
            placeholder="Ex: Reposição de estoque"
            value={formData.notes}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
          />
        </div>
      </Modal>

      <style>{`
        .mov-page { padding: 1.5rem; }
        .mov-filter { margin-bottom: 1rem; max-width: 200px; }
        .mov-table { overflow-x: auto; }
        .mov-table table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .mov-table th { padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; text-align: left; position: sticky; top: 0; z-index: 1;}
        .mov-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .mov-table tbody tr:hover td { background: #eceef0; }
        .mov-pagination { display: flex; align-items: center; justify-content: center; gap: 1rem; padding-top: 1rem; }
        .mov-page-info { font-size: 0.8125rem; color: #43474e; }
        .mov-form { display: flex; flex-direction: column; gap: 1rem; }
        .mov-picked { display: flex; align-items: center; justify-content: space-between; gap: 1rem; border: 1px solid #c4c6cf; border-radius: 4px; padding: 0.5rem 0.75rem; background: #f7f9fb; }
        .mov-lookup { list-style: none; margin: 0.25rem 0 0; padding: 0; border: 1px solid #c4c6cf; border-radius: 4px; background: #fff; max-height: 200px; overflow-y: auto; }
        .mov-lookup li + li { border-top: 1px solid #eceef0; }
        .mov-lookup button { display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; width: 100%; padding: 0.5rem 0.75rem; background: none; border: none; text-align: left; cursor: pointer; font: inherit; }
        .mov-lookup button:hover { background: #f2f4f6; }
        .mov-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; }
        .state-error { text-align: center; padding: 2rem; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty { text-align: center; padding: 2rem; color: #74777f; }
        .state-empty .state-detail { font-size: 0.875rem; margin-top: 0.5rem; }
      `}</style>
    </div>
  );
}
